import React, { useState, useEffect } from "react";
import { User } from "../../Context/UserContext";
import useAPI from "../../Hooks/useAPI";
import {
  PROFESSIONS,
  INTERESTS,
  Profession,
  Interest,
} from "../../constants/userPreferences";
import "./Profile.css";
import Input from "../../Components/Input/Input";
import Button2 from "../../Components/Button2/Button2";

interface ProfileFormProps {
  user: User;
  onSubmit: (formData: any) => Promise<void>;
  onCancel: () => void;
}

// Define props for States
interface StateOption {
  stateprovinceabbrv: string;
  stateprovincename: string;
}

const ProfileForm: React.FC<ProfileFormProps> = ({
  user,
  onSubmit,
  onCancel,
}) => {
  const { getStateProvince } = useAPI();

  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  const [states, setStates] = useState<StateOption[]>([]);
  const [formData, setFormData] = useState({
    firstname: "",
    middlename: "",
    lastname: "",
    streetaddress: "",
    streetaddress2: "",
    city: "",
    state: "",
    zip: "",
    country: "",
    phone: "",
    companyName: "",
    companyType: "",
    title: "",
    profession: [] as Profession[],
    professionOther: "",
    interests: [] as Interest[],
    interestsOther: "",
    activeind: true,
    entityid: "",
  });

  useEffect(() => {
    setFormData({
      firstname: user.firstname || "",
      middlename: user.middlename || "",
      lastname: user.lastname || "",
      streetaddress: user.streetaddress || "",
      streetaddress2: user.streetaddress2 || "",
      city: user.city || "",
      state: user.state || "",
      zip: user.zip || "",
      country: user.country || "",
      phone: user.phone || "",
      companyName: user.companyName || "",
      companyType: user.companyType || "",
      title: user.title || "",
      profession: (user.profession || []) as Profession[],
      professionOther: user.professionOther || "",
      interests: (user.interests || []) as Interest[],
      interestsOther: user.interestsOther || "",
      activeind: user.activeind || true,
      entityid: user.entityid || "",
    });
  }, [user]);

  // Fetch states
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await getStateProvince();
        setStates(response);
      } catch (error) {
        console.error("Error loading states:", error);
      }
    };
    fetchStates();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (
    type: "profession" | "interests",
    value: Profession | Interest,
  ) => {
    setFormData((prev) => {
      if (type === "profession") {
        const currentArray = prev.profession;
        const newArray = currentArray.includes(value as Profession)
          ? currentArray.filter((item: Profession) => item !== value)
          : [...currentArray, value as Profession];

        return {
          ...prev,
          profession: newArray,
        };
      } else {
        const currentArray = prev.interests;
        const newArray = currentArray.includes(value as Interest)
          ? currentArray.filter((item: Interest) => item !== value)
          : [...currentArray, value as Interest];

        return {
          ...prev,
          interests: newArray,
        };
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    try {
      const updatedUserData = {
        ...formData,
      };

      await onSubmit(updatedUserData);
      setSuccess("Profile updated successfully!");

      setTimeout(() => {
        setSuccess("");
      }, 3000);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "An error occurred while updating profile",
      );
    }
  };

  return (
    <div className="profile-card">
      <form onSubmit={handleSubmit} className="profile-form">
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div className="form-group">
          <label>First Name *</label>
          <Input
            label=""
            type="text"
            id="firstname"
            placeholder="Enter first name"
            required={true}
            name="firstname"
            value={formData.firstname}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>Middle Name</label>
          <Input
            label=""
            type="text"
            id="middlename"
            placeholder="Enter middle name"
            name="middlename"
            value={formData.middlename}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>Last Name *</label>
          <Input
            label=""
            type="text"
            id="lastname"
            placeholder="Enter last name"
            name="lastname"
            value={formData.lastname}
            onChange={handleChange}
            required={true}
          />
        </div>

        <div className="form-group">
          <label>Address</label>
          <Input
            label=""
            type="text"
            id="streetaddress"
            placeholder="Enter address"
            name="streetaddress"
            value={formData.streetaddress}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>Address 2</label>
          <Input
            label=""
            type="text"
            id="streetaddress2"
            placeholder="Enter address 2"
            name="streetaddress2"
            value={formData.streetaddress2}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>City</label>
          <Input
            label=""
            type="text"
            id="city"
            placeholder="Enter city"
            name="city"
            value={formData.city}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>State</label>
          <select
            id="state"
            name="state"
            value={formData.state}
            onChange={handleChange}
          >
            <option value="">-- Select State --</option>
            {states.map((s) => (
              <option key={s.stateprovinceabbrv} value={s.stateprovinceabbrv}>
                {s.stateprovincename}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Zip</label>
          <Input
            label=""
            type="text"
            id="zip"
            placeholder="Enter zip"
            name="zip"
            value={formData.zip}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>Country</label>
          <Input
            label=""
            type="text"
            id="country"
            placeholder="Enter country"
            name="country"
            value={formData.country}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>Phone</label>
          <Input
            label=""
            type="tel"
            id="phone"
            placeholder="Enter phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>Company Name</label>
          <Input
            label=""
            type="text"
            id="companyName"
            placeholder="Enter company name"
            name="companyName"
            value={formData.companyName}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>Company Type</label>
          <Input
            label=""
            type="text"
            id="companyType"
            placeholder="Enter company type"
            name="companyType"
            value={formData.companyType}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>Title</label>
          <Input
            label=""
            type="text"
            id="title"
            placeholder="Enter title"
            name="title"
            value={formData.title}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>Professions</label>
          <div className="checkbox-group">
            {PROFESSIONS.map((prof) => (
              <div key={prof} className="checkbox-item">
                <input
                  type="checkbox"
                  id={`prof-${prof}`}
                  checked={formData.profession.includes(prof)}
                  onChange={() => handleCheckboxChange("profession", prof)}
                  className="checkboxCtrl"
                />
                <label htmlFor={`prof-${prof}`}> {prof}</label>
              </div>
            ))}
          </div>
          <label></label>
          {formData.profession.includes("Other") && (
            <Input
              label=""
              type="text"
              id="professionOther"
              placeholder="Please specify"
              name="professionOther"
              value={formData.professionOther}
              onChange={handleChange}
            />
          )}
        </div>

        <div className="form-group">
          <label>Interests</label>
          <div className="checkbox-group">
            {INTERESTS.map((interest) => (
              <div key={interest} className="checkbox-item">
                <input
                  type="checkbox"
                  id={`interest-${interest}`}
                  checked={formData.interests.includes(interest)}
                  onChange={() => handleCheckboxChange("interests", interest)}
                  className="checkboxCtrl"
                />
                <label htmlFor={`interest-${interest}`}> {interest}</label>
              </div>
            ))}
          </div>
          <label></label>
          {formData.interests.includes("Other") && (
            <Input
              label=""
              type="text"
              id="interestsOther"
              placeholder="Please specify"
              name="interestsOther"
              value={formData.interestsOther}
              onChange={handleChange}
            />
          )}
        </div>

        <div className="form-actions">
          <Button2
            name="submit"
            text="Save Changes"
            type="button"
            classname="submit-btn"
          />

          <Button2
            name="submit"
            text="Cancel"
            type="button"
            classname="cancel-btn"
            onClick={onCancel}
          />
        </div>
      </form>
    </div>
  );
};

export default ProfileForm;
